"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useRouter, usePathname } from "next/navigation";
import MobileHeader from "../molecules/MobileHeader";
import MobileDrawer from "../molecules/MobileDrawer";
import Sidebar from "../organisms/Sidebar";
import TabNavigation from "../organisms/TabNavigation";
import Button from "../atoms/Button";
import { logout } from "@/lib/auth/client";
import { resetSessionVerified } from "@/app/components/hoc/withProtectedRoute";
import type { TabName } from "../types";
import type { MouseEvent as ReactMouseEvent } from "react";
import { trackLogoutEvent } from "@/lib/analytics/gtag";
import { ModalProvider, useModal } from "../context/ModalContext";
import { MembershipProvider } from "../context/MembershipContext";
import { ProfileProvider } from "../context/ProfileContext";
import { VerificationProvider } from "../context/VerificationContext";
import { GuestPassProvider } from "../context/GuestPassContext";
import EmailVerificationBanner from "../organisms/EmailVerificationBanner";

/** Ordered list of tabs available in the mobile drawer. */
const tabs: TabName[] = ["Dashboard", "Membership", "Visits", "Profile"];

const pathnameToTab: Record<string, TabName> = {
  "/dashboard": "Dashboard",
  "/membership": "Membership",
  "/visits": "Visits",
  "/profile": "Profile",
};

interface DashboardLayoutProps {
  children: React.ReactNode;
}

/**
 * @component DashboardLayoutInner
 * @description Inner shell that consumes ModalProvider. Separated so the provider
 * can wrap it while this component uses useModal().
 */
function DashboardLayoutInner({ children }: DashboardLayoutProps) {
  const { openModal, closeModal } = useModal();
  const router = useRouter();
  const pathname = usePathname();
  const activeTab: TabName = pathnameToTab[pathname] ?? "Dashboard";
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const logoutTriggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isLogoutModalOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isLoggingOut) {
        setIsLogoutModalOpen(false);
        logoutTriggerRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isLogoutModalOpen, isLoggingOut]);

  useEffect(() => {
    if (menuOpen || isLogoutModalOpen) {
      document.body.style.overflow = "hidden";
      document.body.setAttribute("data-scroll-locked", "1");
    } else {
      document.body.style.overflow = "";
      document.body.removeAttribute("data-scroll-locked");
    }
    return () => {
      document.body.style.overflow = "";
      document.body.removeAttribute("data-scroll-locked");
    };
  }, [menuOpen, isLogoutModalOpen]);

  /**
   * @method handleCloseMenu
   * @description Closes the mobile drawer and returns focus to menu toggle.
   * @returns False when logout is in progress; otherwise true after closing.
   */
  const handleCloseMenu = useCallback(() => {
    setMenuOpen(false);
    menuToggleRef.current?.focus();
  }, []);

  /**
   * @method handleOpenLogoutModal
   * @description Opens the logout confirmation modal.
   * @returns False when logout is in progress; otherwise true after closing.
   */
  const handleOpenLogoutModal = useCallback(
    (event?: ReactMouseEvent<HTMLButtonElement>) => {
      if (event?.currentTarget) {
        logoutTriggerRef.current = event.currentTarget;
      }

      setMenuOpen(false);
      setIsLogoutModalOpen(true);
      trackLogoutEvent("modal_opened");
    },
    [],
  );

  /**
   * @method handleCloseLogoutModal
   * @description Closes the logout confirmation modal.
   * @returns False when logout is in progress; otherwise true after closing.
   */
  const handleCloseLogoutModal = useCallback(() => {
    if (isLoggingOut) return false;
    setIsLogoutModalOpen(false);
    trackLogoutEvent("cancelled");
    return true;
  }, [isLoggingOut]);

  /**
   * @method handleLogout
   * @description Logs out the user and redirects to /login.
   * @returns A promise resolved when logout flow completes.
   */
  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    trackLogoutEvent("confirmed");
    await logout();
    resetSessionVerified();
    router.replace("/login");
  }, [router]);

  // Register logout modal with the shared backdrop
  useEffect(() => {
    if (isLogoutModalOpen) {
      openModal(handleCloseLogoutModal);
    } else {
      closeModal();
    }
    return () => closeModal();
  }, [isLogoutModalOpen, openModal, closeModal, handleCloseLogoutModal]);

  return (
    <div className="min-h-screen bg-canvas overflow-x-clip flex flex-col">
      <MobileHeader
        ref={menuToggleRef}
        menuOpen={menuOpen}
        onMenuToggle={() => setMenuOpen((prev) => !prev)}
      />

      {menuOpen && (
        <MobileDrawer
          tabs={tabs}
          activeTab={activeTab}
          onClose={handleCloseMenu}
          onLogoutRequest={handleOpenLogoutModal}
        />
      )}

      <div className="w-full max-w-[1400px] mx-auto lg:flex min-h-screen bg-white">
        {/* Sidebar - conditionally shows content based on active tab */}
        <Sidebar activeTab={activeTab} />

        <main className="z-1 w-full flex-1 min-h-screen transition-all duration-300">
          <EmailVerificationBanner />
          <TabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onLogoutRequest={handleOpenLogoutModal}
          />

          <div
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
            className="bg-white"
          >
            {children}
          </div>
        </main>
      </div>

      {isLogoutModalOpen && (
        <div className="fixed inset-0 z-51 flex items-center justify-center px-4 pointer-events-none">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-dialog-title"
            className="pointer-events-auto bg-white w-full max-w-sm md:max-w-md border border-card-stroke shadow-lg p-8 flex flex-col items-center gap-6"
          >
            <h2 id="logout-dialog-title" className="text-heading text-charcoal">
              Log out?
            </h2>
            <div className="flex flex-col w-full gap-3">
              <Button
                variant="primary"
                size="full"
                onClick={handleLogout}
                loading={isLoggingOut}
              >
                Yes, Log Out
              </Button>
              <Button
                variant="outline"
                size="full"
                onClick={handleCloseLogoutModal}
                disabled={isLoggingOut}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * @component DashboardLayout
 * @description Top-level page layout template for the member dashboard.
 * Wraps DashboardLayoutInner with ModalProvider so all descendant components
 * can use useModal() to register with the single shared backdrop.
 * @param children - Tab panel content to render in the main content area.
 * @returns The full dashboard layout wrapped in ModalProvider.
 */
export default function DashboardLayout({ children }: DashboardLayoutProps) {
  return (
    <ModalProvider>
      <MembershipProvider>
        <VerificationProvider>
          <ProfileProvider>
            <GuestPassProvider>
              <DashboardLayoutInner>{children}</DashboardLayoutInner>
            </GuestPassProvider>
          </ProfileProvider>
        </VerificationProvider>
      </MembershipProvider>
    </ModalProvider>
  );
}
